import { useState, useEffect } from "react";
import { X, BookOpen, Plus, Search, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLibrary, type LibrarySection } from "@/core/hooks/useLibrary";

// ─── Labels des catégories ────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  "analyses-eau":     "Analyses eau",
  "principe-mesure":  "Principe de la mesure",
  "rappel-normes":    "Rappel des normes",
  "mission":          "Mission",
  "conclusions":      "Conclusions",
  "custom":           "Mes blocs",
};

const CATEGORY_ORDER = [
  "analyses-eau",
  "principe-mesure",
  "rappel-normes",
  "mission",
  "conclusions",
  "custom",
];

// ─── Extrait la première ligne de texte du Tiptap JSON ───────

function extractPreview(content: unknown): string {
  try {
    const doc = content as { type: string; content?: unknown[] };
    for (const node of doc.content ?? []) {
      const n = node as { type: string; content?: Array<{ type: string; text?: string }> };
      if (n.type === "paragraph" || n.type === "heading") {
        const text = (n.content ?? [])
          .filter((c) => c.type === "text" && c.text)
          .map((c) => c.text)
          .join("");
        if (text.trim()) return text.trim().slice(0, 100) + (text.length > 100 ? "…" : "");
      }
    }
  } catch { /* contenu invalide */ }
  return "Aucun aperçu disponible";
}

// ─── Composant ────────────────────────────────────────────────

interface LibraryDrawerProps {
  open: boolean;
  afterSection: string;
  onInsert: (section: LibrarySection, afterSection: string) => void;
  onClose: () => void;
}

export function LibraryDrawer({ open, afterSection, onInsert, onClose }: LibraryDrawerProps) {
  const { sections, loading, error } = useLibrary();
  const [search, setSearch] = useState("");
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

  // Reset l'état d'ouverture à chaque ouverture du drawer
  useEffect(() => {
    if (open) setOpenCategories(new Set());
  }, [open]);

  function toggleCategory(cat: string) {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  // Fermer sur Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Scroll lock
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const filtered = search.trim()
    ? sections.filter((s) =>
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        CATEGORY_LABELS[s.category]?.toLowerCase().includes(search.toLowerCase())
      )
    : sections;

  // Grouper par catégorie dans l'ordre souhaité
  const grouped: Array<{ category: string; label: string; items: LibrarySection[] }> = [];
  for (const cat of CATEGORY_ORDER) {
    const items = filtered.filter((s) => s.category === cat);
    if (items.length > 0) {
      grouped.push({ category: cat, label: CATEGORY_LABELS[cat] ?? cat, items });
    }
  }
  // Catégories non listées dans CATEGORY_ORDER : 1 groupe par catégorie custom,
  // libellé = nom de la catégorie tel que saisi (au lieu d'une poubelle "Autres").
  const knownCats = new Set(CATEGORY_ORDER);
  const extraCats = new Map<string, LibrarySection[]>();
  for (const s of filtered) {
    if (knownCats.has(s.category)) continue;
    const key = s.category || "other";
    if (!extraCats.has(key)) extraCats.set(key, []);
    extraCats.get(key)!.push(s);
  }
  for (const [cat, items] of extraCats) {
    grouped.push({
      category: cat,
      label: CATEGORY_LABELS[cat] ?? cat,
      items,
    });
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-background shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <BookOpen className="size-5 text-primary" />
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-base">Librairie de contenus</h2>
            <p className="text-xs text-muted-foreground truncate">
              Insertion après : <span className="font-medium">{afterSection}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un bloc…"
              className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-input bg-background focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
          {loading && (
            <p className="text-sm text-muted-foreground text-center py-8">Chargement…</p>
          )}
          {error && (
            <p className="text-sm text-destructive text-center py-4">{error}</p>
          )}
          {!loading && !error && grouped.length === 0 && (
            <div className="text-center py-12 space-y-2">
              <BookOpen className="size-8 text-muted-foreground/50 mx-auto" />
              <p className="text-sm text-muted-foreground">
                {search ? "Aucun résultat pour cette recherche" : "La librairie est vide"}
              </p>
            </div>
          )}

          {grouped.map(({ category, label, items }) => {
            // En mode recherche, tout est déplié pour montrer les résultats.
            // Sinon, accordéon : replié par défaut.
            const isOpen = search.trim() ? true : openCategories.has(category);
            return (
              <div key={category}>
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  disabled={!!search.trim()}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-left hover:bg-accent/40 transition-colors disabled:cursor-default disabled:hover:bg-card"
                >
                  <span className="flex items-center gap-2">
                    {isOpen ? (
                      <ChevronDown className="size-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="size-4 text-muted-foreground" />
                    )}
                    <span className="text-sm font-semibold text-foreground">
                      {label}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {items.length}
                    </span>
                  </span>
                </button>
                {isOpen && (
                  <div className="mt-2 space-y-2 pl-2">
                    {items.map((section) => (
                      <LibrarySectionCard
                        key={section.id}
                        section={section}
                        onInsert={() => onInsert(section, afterSection)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Card individuelle ────────────────────────────────────────

function LibrarySectionCard({
  section,
  onInsert,
}: {
  section: LibrarySection;
  onInsert: () => void;
}) {
  const preview = extractPreview(section.content);

  return (
    <div className="rounded-lg border border-border bg-card p-3 hover:border-primary/40 hover:bg-card/80 transition-colors group">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{section.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
            {preview}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onInsert}
          className="shrink-0 gap-1.5 text-xs h-7"
        >
          <Plus className="size-3" />
          Insérer
        </Button>
      </div>
    </div>
  );
}
